import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Receipt, Plus, ArrowLeft, FileDown, Lock, Trash2, Camera, Paperclip, ChevronRight, DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
const money = (v) => `$${Number(v || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };

export default function ExpenseClaims() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");
  const authHeader = { Authorization: `Bearer ${token}` };
  const isOwner = ["admin", "company_owner"].includes(user?.role);
  const canUse = ["admin", "company_owner", "site_manager"].includes(user?.role);

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [claims, setClaims] = useState([]);
  const [view, setView] = useState("list");
  const [current, setCurrent] = useState(null);

  const [showNewItem, setShowNewItem] = useState(false);
  const [itemForm, setItemForm] = useState({ date: todayStr(), store: "", description: "", invoice_number: "", amount: "" });
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/sites`, { headers: authHeader }).then((r) => r.ok && r.json()).then((d) => {
      if (d) { setSites(d); if (d.length) setSelectedSite(d[0].id); }
    });
  }, []); // eslint-disable-line

  const loadClaims = async () => {
    if (!selectedSite) return;
    const r = await fetch(`${API}/expense-claims/${selectedSite}`, { headers: authHeader });
    if (r.ok) setClaims(await r.json());
  };
  useEffect(() => { loadClaims(); }, [selectedSite]); // eslint-disable-line

  const openDetail = async (claim) => {
    const r = await fetch(`${API}/expense-claims/${claim.site_id}/${claim.id}`, { headers: authHeader });
    if (r.ok) { setCurrent(await r.json()); setView("detail"); }
  };
  const refresh = async () => {
    const r = await fetch(`${API}/expense-claims/${current.site_id}/${current.id}`, { headers: authHeader });
    if (r.ok) setCurrent(await r.json());
  };

  const startClaim = async () => {
    const r = await fetch(`${API}/expense-claims`, {
      method: "POST", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ site_id: selectedSite }),
    });
    if (r.ok) { const c = await r.json(); toast.success(`Claim ${c.claim_id} started`); setCurrent(c); setView("detail"); loadClaims(); }
    else toast.error("Could not start claim");
  };

  const addItem = async () => {
    if (!itemForm.store.trim() || !itemForm.description.trim() || !itemForm.amount) { toast.error("Store, description and amount are required"); return; }
    if (!receiptFile) { toast.error("Attach a receipt"); return; }
    if (receiptFile.size > 10 * 1024 * 1024) { toast.error("Receipt exceeds 10 MB"); return; }
    const fd = new FormData();
    fd.append("date", itemForm.date);
    fd.append("store", itemForm.store);
    fd.append("description", itemForm.description);
    fd.append("invoice_number", itemForm.invoice_number || "");
    fd.append("amount", itemForm.amount);
    fd.append("receipt", receiptFile);
    setUploading(true);
    const r = await fetch(`${API}/expense-claims/${current.id}/line-items`, { method: "POST", headers: authHeader, body: fd });
    setUploading(false);
    if (r.ok) {
      setCurrent(await r.json()); setShowNewItem(false);
      setItemForm({ date: todayStr(), store: "", description: "", invoice_number: "", amount: "" });
      setReceiptFile(null);
    } else { const e = await r.json(); toast.error(e.detail || "Could not add line item"); }
  };

  const deleteItem = async (itemId) => {
    const r = await fetch(`${API}/expense-claims/${current.id}/line-items/${itemId}`, { method: "DELETE", headers: authHeader });
    if (r.ok) setCurrent(await r.json());
    else toast.error("Could not remove line item");
  };

  const produce = async () => {
    const r = await fetch(`${API}/expense-claims/${current.id}/produce`, { method: "POST", headers: authHeader });
    if (r.ok) { setCurrent(await r.json()); closePreview(); loadClaims(); toast.success("Claim produced & locked"); }
    else { const e = await r.json(); toast.error(e.detail || "Could not produce claim"); }
  };

  const openPreview = async () => {
    if ((current.line_items || []).length === 0) { toast.error("Add at least one line item first"); return; }
    setPreviewLoading(true); setShowPreview(true);
    const r = await fetch(`${API}/expense-claims/${current.id}/pdf/export`, { headers: authHeader });
    setPreviewLoading(false);
    if (!r.ok) { toast.error("Could not generate preview"); setShowPreview(false); return; }
    const blob = await r.blob();
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setShowPreview(false);
  };

  const toggleTracking = async (field, value) => {
    const r = await fetch(`${API}/expense-claims/${current.id}/tracking`, {
      method: "PUT", headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    });
    if (r.ok) setCurrent(await r.json());
    else toast.error("Could not update tracking");
  };

  const downloadPdf = async (id, claimId) => {
    const r = await fetch(`${API}/expense-claims/${id}/pdf/export`, { headers: authHeader });
    if (!r.ok) { toast.error("Could not generate PDF"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `expense_claim_${claimId || id}.pdf`; a.rel = "noopener";
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
  };

  // ---------------- DETAIL ----------------
  if (view === "detail" && current) {
    const items = current.line_items || [];
    const locked = current.status === "locked";
    return (
      <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={() => { setView("list"); setCurrent(null); loadClaims(); }} data-testid="exp-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to claims
            </Button>
            <div className="flex gap-2">
              {locked && (
                <Button variant="outline" onClick={() => downloadPdf(current.id, current.claim_id)} data-testid="exp-pdf-btn">
                  <FileDown className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              )}
              {!locked && (
                <Button onClick={openPreview} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exp-produce-btn">
                  <Lock className="w-4 h-4 mr-2" /> Preview & produce
                </Button>
              )}
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-2xl text-slate-800 flex items-center gap-2">
                  <Receipt className="w-6 h-6 text-emerald-600" /> {current.claim_id}
                </CardTitle>
                <Badge className={locked ? "bg-slate-700" : "bg-amber-500"} data-testid="exp-status-badge">
                  {locked ? "Locked" : "Draft"}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">Site Manager: {current.created_by_name}</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-700">Line items</h3>
                {!locked && (
                  <Button size="sm" variant="outline" onClick={() => setShowNewItem(true)} data-testid="exp-add-item-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add line item
                  </Button>
                )}
              </div>
              {items.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="exp-empty-items">No line items yet. Add each purchase with its receipt.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Store</TableHead><TableHead>Description</TableHead>
                      <TableHead>Invoice #</TableHead><TableHead className="text-right">Amount</TableHead>
                      <TableHead>Receipt</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((li) => (
                      <TableRow key={li.id} data-testid={`exp-item-${li.id}`}>
                        <TableCell>{fmtDate(li.date)}</TableCell>
                        <TableCell>{li.store}</TableCell>
                        <TableCell>{li.description}</TableCell>
                        <TableCell>{li.invoice_number || "—"}</TableCell>
                        <TableCell className="text-right">{money(li.amount)}</TableCell>
                        <TableCell><Paperclip className="w-4 h-4 text-emerald-600" title={li.receipt_filename} /></TableCell>
                        <TableCell>
                          {!locked && (
                            <button onClick={() => deleteItem(li.id)} className="text-slate-400 hover:text-red-600" data-testid={`exp-delete-item-${li.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-semibold">
                      <TableCell colSpan={4} className="text-right">Total</TableCell>
                      <TableCell className="text-right" data-testid="exp-total">{money(current.total)}</TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {locked && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Your tracking <span className="text-sm font-normal text-slate-500">(personal only — not an approval step)</span></CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={!!current.submitted_to_accounts} onCheckedChange={(v) => toggleTracking("submitted_to_accounts", !!v)} data-testid="exp-track-submitted" />
                  <span className="text-slate-700">Submitted to accounts</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={!!current.payment_received} onCheckedChange={(v) => toggleTracking("payment_received", !!v)} data-testid="exp-track-paid" />
                  <span className="text-slate-700">Payment received</span>
                </label>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Add line item dialog */}
        <Dialog open={showNewItem} onOpenChange={setShowNewItem}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="exp-item-dialog">
            <DialogHeader><DialogTitle>Add line item</DialogTitle>
              <DialogDescription>One row per purchase. A receipt is required.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={itemForm.date} onChange={(e) => setItemForm({ ...itemForm, date: e.target.value })} data-testid="exp-item-date" /></div>
                <div><Label>Amount (AUD)</Label><Input type="number" step="0.01" min="0" value={itemForm.amount} onChange={(e) => setItemForm({ ...itemForm, amount: e.target.value })} data-testid="exp-item-amount" /></div>
              </div>
              <div><Label>Store</Label><Input value={itemForm.store} onChange={(e) => setItemForm({ ...itemForm, store: e.target.value })} data-testid="exp-item-store" /></div>
              <div><Label>Description</Label><Input value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} data-testid="exp-item-desc" /></div>
              <div><Label>Invoice # (optional)</Label><Input value={itemForm.invoice_number} onChange={(e) => setItemForm({ ...itemForm, invoice_number: e.target.value })} data-testid="exp-item-invoice" /></div>
              <div>
                <Label>Receipt (photo or PDF)</Label>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment"
                  onChange={(e) => setReceiptFile(e.target.files[0] || null)} className="hidden" data-testid="exp-item-receipt-input" />
                <div className="flex items-center gap-2 mt-1">
                  <Button type="button" variant="outline" onClick={() => fileRef.current && fileRef.current.click()} data-testid="exp-item-receipt-btn">
                    <Camera className="w-4 h-4 mr-1" /> Attach receipt
                  </Button>
                  {receiptFile && <span className="text-sm text-slate-600 truncate max-w-[200px]">{receiptFile.name}</span>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewItem(false)}>Cancel</Button>
              <Button onClick={addItem} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exp-item-save">{uploading ? "Uploading…" : "Add line item"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview before lock */}
        <Dialog open={showPreview} onOpenChange={(o) => { if (!o) closePreview(); }}>
          <DialogContent className="max-w-4xl" data-testid="exp-produce-dialog">
            <DialogHeader><DialogTitle>Preview claim {current.claim_id}</DialogTitle>
              <DialogDescription>
                This is a draft preview. Review it, keep editing if needed, or produce to lock it permanently (no more line items).
              </DialogDescription>
            </DialogHeader>
            <div className="border rounded-lg bg-slate-100 h-[60vh] overflow-hidden" data-testid="exp-preview-frame">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full text-slate-500">Generating preview…</div>
              ) : (
                previewUrl && <iframe title="claim-preview" src={previewUrl} className="w-full h-full" />
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closePreview} data-testid="exp-preview-edit">Keep editing</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={produce} data-testid="exp-produce-confirm">
                <Lock className="w-4 h-4 mr-2" /> Produce & lock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------------- LIST ----------------
  if (!canUse) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
        <Card className="max-w-md text-center" data-testid="exp-no-access">
          <CardContent className="pt-8 pb-8">
            <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-800">Expense Claims isn't available for your role</h2>
            <p className="text-slate-500 mt-2">This module is for Site Managers and Company Owners.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Receipt className="w-9 h-9 text-emerald-600" />
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Expense Claims</h1>
              <p className="text-slate-600">Your out-of-pocket purchases — add receipts, produce one clean PDF for accounts.</p>
            </div>
          </div>
          {selectedSite && (
            <Button onClick={startClaim} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exp-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New claim
            </Button>
          )}
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="exp-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isOwner && (
              <div className="flex items-end">
                <p className="text-sm text-slate-500">As an owner you can see <span className="font-medium">all</span> site managers' claims for this site (view only).</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            {claims.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="exp-empty-list">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No expense claims yet for this site.</p>
                <p className="text-sm">Click "New claim" to start one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Claim</TableHead>{isOwner && <TableHead>Site Manager</TableHead>}
                    <TableHead>Items</TableHead><TableHead className="text-right">Total</TableHead>
                    <TableHead>Tracking</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {claims.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(c)} data-testid={`exp-row-${c.id}`}>
                      <TableCell className="font-medium">{c.claim_id}</TableCell>
                      {isOwner && <TableCell>{c.created_by_name}</TableCell>}
                      <TableCell>{(c.line_items || []).length}</TableCell>
                      <TableCell className="text-right">{money(c.total)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {c.submitted_to_accounts && <Badge variant="secondary" className="text-xs">Submitted</Badge>}
                          {c.payment_received && <Badge className="bg-emerald-600 text-xs">Paid</Badge>}
                          {!c.submitted_to_accounts && !c.payment_received && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={c.status === "locked" ? "bg-slate-700" : "bg-amber-500"}>
                          {c.status === "locked" ? "Locked" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
